import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { login } from '../services/api';
import toast from 'react-hot-toast';

const QuoteologyLogo = ({ size = 48 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Quoteology">
    <circle cx="28" cy="28" r="25" fill="none" stroke="#3FD39A" strokeOpacity="0.35" strokeWidth="1.6"/>
    <g>
      <circle cx="53" cy="28" r="4.2" fill="#3FD39A"/>
      <animateTransform attributeName="transform" attributeType="XML" type="rotate"
        from="0 28 28" to="360 28 28" dur="5s" repeatCount="indefinite"/>
    </g>
    <circle cx="28" cy="28" r="20" fill="none" stroke="#FFFFFF" strokeWidth="6.5"/>
    <rect x="18.6" y="31" width="3.2" height="6"  rx="1.2" fill="#FFFFFF"/>
    <rect x="23.8" y="26" width="3.2" height="11" rx="1.2" fill="#FFFFFF"/>
    <rect x="29"   y="22" width="3.2" height="15" rx="1.2" fill="#FFFFFF"/>
    <rect x="34.2" y="17" width="3.2" height="20" rx="1.2" fill="#3FD39A"/>
    <line x1="40" y1="40" x2="52" y2="52" stroke="#FFFFFF" strokeWidth="6.5" strokeLinecap="round"/>
  </svg>
);

export default function LoginPage() {
  const [form,     setForm]     = useState({ email:'', password:'' });
  const [loading,  setLoading]  = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const { loginUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await login(form);
      loginUser(data.user, data.token, data.organisation || null, data.admin_email || null);
      toast.success(`Welcome, ${data.user.name}!`);
      navigate('/');
    } catch(err) {
      toast.error(err.response?.data?.message || 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <div style={{
      minHeight:'100vh',
      background:'#0C1220',
      display:'flex',
      flexDirection: isMobile ? 'column' : 'row',
      position:'relative',
      overflowY:'auto',
    }}>
      {/* Ambient grid */}
      <div style={{
        position:'fixed',inset:0,
        backgroundImage:`
          linear-gradient(rgba(14,165,233,.04) 1px,transparent 1px),
          linear-gradient(90deg,rgba(14,165,233,.04) 1px,transparent 1px)
        `,
        backgroundSize:'52px 52px',
        pointerEvents:'none',zIndex:0,
      }}/>
      <div style={{position:'fixed',top:'-8%',left:'-4%',width:560,height:560,borderRadius:'50%',background:'radial-gradient(circle,rgba(14,165,233,.09) 0%,transparent 65%)',pointerEvents:'none',zIndex:0}}/>
      <div style={{position:'fixed',bottom:'-8%',right:'-4%',width:440,height:440,borderRadius:'50%',background:'radial-gradient(circle,rgba(63,211,154,.06) 0%,transparent 65%)',pointerEvents:'none',zIndex:0}}/>

      {/* Left panel — branding */}
      <div style={{
        flex:1,
        display:'flex',flexDirection:'column',
        alignItems: isMobile ? 'center' : 'flex-start',
        justifyContent:'center',
        padding: isMobile ? '52px 28px 36px' : '60px 72px',
        position:'relative',zIndex:1,
        textAlign: isMobile ? 'center' : 'left',
      }}>

        {/* Brand lockup */}
        <div style={{
          display:'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems:'center',
          gap: isMobile ? 12 : 18,
          marginBottom: isMobile ? 28 : 36,
          justifyContent: isMobile ? 'center' : 'flex-start',
        }}>
          <div style={{
            background:'rgba(14,165,233,.12)',
            border:'1px solid rgba(14,165,233,.2)',
            borderRadius:15,
            padding: isMobile ? 9 : 11,
            display:'inline-flex',flexShrink:0,
          }}>
            <QuoteologyLogo size={isMobile ? 40 : 48}/>
          </div>
          <div style={{textAlign: isMobile ? 'center' : 'left'}}>
            <div style={{
              color:'#fff',
              fontSize: isMobile ? 38 : 48,
              fontWeight:800,
              letterSpacing:'-.9px',
              lineHeight:1.0,
            }}>
              Quoteology
            </div>
            <div style={{
              color:'#38BDF8',
              fontSize:11.5,
              fontWeight:600,
              letterSpacing:'.13em',
              textTransform:'uppercase',
              marginTop:6,
            }}>
              Global Cost Platform
            </div>
          </div>
        </div>

        {/* Tagline */}
        <h1 style={{
          color:'#fff',
          fontSize: isMobile ? 30 : 46,
          fontWeight:800,
          letterSpacing:'-1.1px',
          lineHeight:1.1,
          marginBottom: isMobile ? 14 : 18,
          maxWidth:460,
        }}>
          Know the cost.<br/>
          <span style={{color:'#38BDF8'}}>Win the deal.</span>
        </h1>

        <p style={{
          color:'rgba(255,255,255,.5)',
          fontSize: isMobile ? 14 : 16,
          lineHeight:1.75,
          maxWidth:400,
          margin: isMobile ? '0 auto 10px' : '0 0 44px',
        }}>
          Instant contractor cost calculations across 8 countries — tax, social insurance, and compliance built in.
        </p>

        {!isMobile && (
          <div style={{display:'flex',flexWrap:'wrap',gap:9}}>
            {['Saudi Arabia','India','Australia','United States','Netherlands','Japan','Norway','China'].map(c => (
              <span key={c} style={{
                background:'rgba(255,255,255,.06)',
                border:'1px solid rgba(255,255,255,.1)',
                color:'rgba(255,255,255,.55)',
                fontSize:12,padding:'5px 12px',borderRadius:20,letterSpacing:'.01em',
              }}>{c}</span>
            ))}
          </div>
        )}
      </div>

      {/* Right panel — login form */}
      <div style={{
        width: isMobile ? '100%' : 480,
        flexShrink:0,
        display:'flex',alignItems:'center',justifyContent:'center',
        padding: isMobile ? '36px 28px 60px' : '44px 36px',
        background: isMobile ? 'rgba(255,255,255,.02)' : 'rgba(255,255,255,.03)',
        borderLeft: isMobile ? 'none' : '1px solid rgba(255,255,255,.07)',
        borderTop:  isMobile ? '1px solid rgba(255,255,255,.07)' : 'none',
        position:'relative',zIndex:1,
      }}>
        <div style={{width:'100%',maxWidth:390}}>

          {!isMobile && (
            <div style={{marginBottom:26}}>
              <h2 style={{color:'#fff',fontSize:22,fontWeight:700,letterSpacing:'-.3px',marginBottom:6}}>Sign in</h2>
              <p style={{color:'rgba(255,255,255,.4)',fontSize:14}}>Enter your credentials to continue</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{marginBottom:16}}>
              <label style={{display:'block',fontSize:11.5,fontWeight:600,color:'rgba(255,255,255,.4)',letterSpacing:'.07em',textTransform:'uppercase',marginBottom:7}}>
                Email
              </label>
              <input
                type="email"
                placeholder="you@quoteology.com"
                value={form.email}
                onChange={e => setForm({...form, email:e.target.value})}
                required
                style={{width:'100%',padding:'12px 15px',background:'rgba(255,255,255,.07)',border:'1.5px solid rgba(255,255,255,.12)',borderRadius:9,fontSize:14.5,color:'#fff',outline:'none',fontFamily:'inherit',transition:'border-color .15s'}}
                onFocus={e => e.target.style.borderColor='#0EA5E9'}
                onBlur={e  => e.target.style.borderColor='rgba(255,255,255,.12)'}
              />
            </div>

            <div style={{marginBottom:26}}>
              <label style={{display:'block',fontSize:11.5,fontWeight:600,color:'rgba(255,255,255,.4)',letterSpacing:'.07em',textTransform:'uppercase',marginBottom:7}}>
                Password
              </label>
              <input
                type="password"
                placeholder="••••••••"
                value={form.password}
                onChange={e => setForm({...form, password:e.target.value})}
                required
                style={{width:'100%',padding:'12px 15px',background:'rgba(255,255,255,.07)',border:'1.5px solid rgba(255,255,255,.12)',borderRadius:9,fontSize:14.5,color:'#fff',outline:'none',fontFamily:'inherit',transition:'border-color .15s'}}
                onFocus={e => e.target.style.borderColor='#0EA5E9'}
                onBlur={e  => e.target.style.borderColor='rgba(255,255,255,.12)'}
              />
            </div>

            <button type="submit" disabled={loading} style={{
              width:'100%',padding:'13px',
              background: loading ? 'rgba(14,165,233,.6)' : '#0EA5E9',
              color:'#fff',border:'none',borderRadius:9,
              fontSize:15,fontWeight:600,
              cursor: loading ? 'not-allowed' : 'pointer',
              display:'flex',alignItems:'center',justifyContent:'center',gap:8,
              transition:'background .15s',letterSpacing:'-.01em',fontFamily:'inherit',
            }}
            onMouseEnter={e => !loading && (e.currentTarget.style.background='#0284C7')}
            onMouseLeave={e => !loading && (e.currentTarget.style.background='#0EA5E9')}>
              {loading ? <><div className="spinner"/>Signing in...</> : 'Sign in →'}
            </button>
          </form>

          <div style={{
            marginTop:12,padding:'13px 15px',
            background:'rgba(255,255,255,.04)',
            border:'1px solid rgba(255,255,255,.08)',
            borderRadius:9,fontSize:12,
            color:'rgba(255,255,255,.35)',lineHeight:1.95,
          }}>
            <span style={{color:'rgba(255,255,255,.5)',fontWeight:600}}>Demo credentials</span><br/>
            Admin: admin@quoteology.com / Admin@123<br/>
            User: user@quoteology.com / User@123
          </div>
        </div>
      </div>
    </div>
  );
}